import React, { useState, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import ClickAwayListener from 'react-click-away-listener';
import styles from './TreeSelect.module.scss';

export interface TreeSelectOption<T> {
    label: string;
    value: string;
    parentOrganizationId?: string | null;
    data?: T;
}

export interface TreeSelectProps<T> {
    label?: string;
    icon?: IconProp;
    error?: string;
    placeholder?: string;
    className?: string;
    options: Array<TreeSelectOption<T>>;
    value?: string;
    disabled?: boolean;
    required?: boolean;
    renderOption?: (option: TreeSelectOption<T>, level: number) => React.ReactNode;
    onChange?: (value: string, option: TreeSelectOption<T>) => void;
}

interface TreeNode<T> {
    option: TreeSelectOption<T>;
    children: TreeNode<T>[];
    level: number;
}

const TreeSelectComponent = <T,>(
    {
        options,
        className,
        renderOption,
        value,
        onChange,
        placeholder,
        label,
        error,
        icon,
        disabled,
        required,
        ...props
    }: TreeSelectProps<T>,
    ref: React.Ref<HTMLInputElement>
) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef<HTMLDivElement>(null);
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    // Expose the hidden input ref to parent components
    React.useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);

    const selectedOption = options.find(option => option.value === value);

    // Build tree structure from flat options
    const treeData = useMemo(() => {
        const buildTree = (
            items: Array<TreeSelectOption<T>>,
            parentId: string | null | undefined = null,
            level = 0
        ): TreeNode<T>[] => {
            return items
                .filter(item => {
                    // Root level items
                    if (parentId === null || parentId === undefined) {
                        return item.parentOrganizationId === null || item.parentOrganizationId === undefined;
                    }
                    // Child items
                    return item.parentOrganizationId === parentId;
                })
                .map(item => ({
                    option: item,
                    children: buildTree(items, item.value, level + 1),
                    level
                }));
        };

        return buildTree(options);
    }, [options]);

    // Flatten tree for rendering
    const flattenTree = (nodes: TreeNode<T>[]): Array<{ option: TreeSelectOption<T>; level: number }> => {
        const result: Array<{ option: TreeSelectOption<T>; level: number }> = [];

        const traverse = (node: TreeNode<T>) => {
            result.push({ option: node.option, level: node.level });
            node.children.forEach(traverse);
        };

        nodes.forEach(traverse);
        return result;
    };

    const flatOptions = useMemo(() => flattenTree(treeData), [treeData]);

    const handleOptionClick = (option: TreeSelectOption<T>) => {
        onChange?.(option.value, option);
        setIsOpen(false);
    };

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className={clsx(styles.root, className)}>
            {label && (
                <Label
                    error={error}
                    required={required}
                >
                    {label}
                </Label>
            )}

            <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                <div
                    className={styles.selectWrapper}
                    ref={selectRef}
                >
                    {/* Hidden input for focus management */}
                    <input
                        ref={hiddenInputRef}
                        type="text"
                        style={{
                            position: 'absolute',
                            opacity: 0,
                            pointerEvents: 'none',
                            width: 0,
                            height: 0
                        }}
                        tabIndex={-1}
                        aria-hidden="true"
                        onFocus={() => selectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                    />
                    <div
                        className={clsx(styles.select, {
                            [styles.selectOpen]: isOpen,
                            [styles.selectFilled]: selectedOption,
                            [styles.selectError]: error,
                            [styles.selectDisabled]: disabled
                        })}
                        onClick={handleToggle}
                    >
                        <div className={styles.selectContent}>
                            {selectedOption ? (
                                renderOption ? (
                                    renderOption(selectedOption, 0)
                                ) : (
                                    selectedOption.label
                                )
                            ) : (
                                <span className={styles.placeholder}>{placeholder}</span>
                            )}
                        </div>

                        <FontAwesomeIcon
                            icon={icon || faChevronDown}
                            className={clsx(styles.icon, {
                                [styles.iconOpen]: isOpen
                            })}
                        />
                    </div>

                    {isOpen && (
                        <div className={styles.dropdown}>
                            {flatOptions.map(({ option, level }) => (
                                <div
                                    key={option.value}
                                    className={clsx(styles.option, {
                                        [styles.optionSelected]: option.value === value
                                    })}
                                    style={{ paddingLeft: `${24 + level * 24}px` }}
                                    onClick={() => handleOptionClick(option)}
                                >
                                    {renderOption ? renderOption(option, level) : option.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </ClickAwayListener>
        </div>
    );
};

// Use forwardRef to support ref forwarding
export const TreeSelect = React.forwardRef(TreeSelectComponent) as <T>(
    props: TreeSelectProps<T> & { ref?: React.Ref<HTMLInputElement> }
) => ReturnType<typeof TreeSelectComponent>;

export default TreeSelect;
